<template lang="pug">
main(
  role="main"
  v-loading="loading"
  :class="{ 'pl-main-menu-width': isWebAppMenu && !isEmbedded && isDesktop, 'embedded-layout': isEmbedded, 'no-padding-layout': noPadding }"
).pos-r.web-app-container
  .main-content_inner-wrap.web-app-menu.web-app-body(
    :class="{ 'menu-open': isWebAppMenu && !isDesktop }"
  )
    div
      web-app-menu(
        v-if="(isWebAppMenu || !isDesktop) && !isEmbedded"
        :items="WebAppMenuItems"
        :isWebAppMenu="isWebAppMenu"
      )
      div.overlay(v-if="isWebAppMenu && !isDesktop && !isEmbedded" @click="setMainMenuCollapse")
      .web-app-sticky-header(v-if="!isEmbedded && stickyHeader && isMobile")
        .web-app-mobile-header-bar(
          :class="{ 'has-gradient-header': hasGradient, 'white-header-mode': whiteMode, 'has-sidebar': isWebAppMenu && isDesktop, 'has-header-leading': !!$slots['header-leading'] }"
        )
          .header-bar-leading
            .header-leading-slot.mobile-header-leading-only(v-if="$slots['header-leading']")
              slot(name="header-leading")
            fai(
              type="fal"
              icon="bars"
              @click="setMainMenuCollapse"
              v-if="!$slots['header-leading'] && !isDesktop && !isWebAppMenu"
              :class="{ 'white-icon': whiteMode }"
            ).size-28.min-size-28.header-menu-btn
          .header-bar-center(v-if="showLogo")
            img(
              src="/images/full-logo.png"
              :class="{ 'white-logo': hasGradient }"
            ).logo-image
          h3.header-bar-center(v-else :class="{ 'white-text': whiteMode }") {{ title }}
          .header-bar-actions
            button.header-cart-btn(
              v-if="showMobileCart"
              @click="goToCart"
              type="button"
              :class="{ 'white-icon-btn': whiteMode }"
              aria-label="View cart"
            )
              .icon-with-badge
                img(src="@/assets/icons/shopping-cart.svg" alt="")
                .cart-badge(v-if="cartItemCount > 0") {{ cartItemCount }}
            slot(name="actions")
              //- actions slot
      .web-app-sticky-header-spacer(v-if="!isEmbedded && stickyHeader && isMobile")
      .row.row-sm.align-items-center.mb-3(
        v-else-if="!isEmbedded"
        :class="{ 'has-gradient-header': hasGradient, 'white-header-mode': whiteMode, 'has-sidebar': isWebAppMenu && isDesktop }"
      )
        .col
          .d-flex.align-items-center
            fai(
              type="fal"
              icon="bars"
              @click="setMainMenuCollapse"
              v-if="!isDesktop && !isWebAppMenu"
              :class="{ 'white-icon': whiteMode }"
            ).size-28.me-2.min-size-28
            div(v-if="showLogo").mb-0
              img(
                src="/images/full-logo.png"
                :class="{ 'white-logo': hasGradient }"
              ).logo-image
            h3(v-else :class="{ 'white-text': whiteMode }").mb-0.mx-auto {{ title }}
        .col-auto.header-actions
          button.header-cart-btn(
            v-if="showMobileCart"
            @click="goToCart"
            type="button"
            :class="{ 'white-icon-btn': whiteMode }"
            aria-label="View cart"
          )
            .icon-with-badge
              img(src="@/assets/icons/shopping-cart.svg" alt="")
              .cart-badge(v-if="cartItemCount > 0") {{ cartItemCount }}
          slot(name="actions")
            //- actions slot

      .grid-2-cols(v-if="($slots['col-left'] || $slots['col-right']) && !isEmbedded")
        .col-has-child.main-col-left
          slot(name="col-left")
            //- col-left slot

        .col-has-child.main-col-right(v-if="$slots['col-right']")
          slot(name="col-right")
            //- col-right slot

      .col-has-child(v-else :class="{ 'p-0': false }")
        slot
</template>

<script lang="ts">
import {
  defineComponent, computed, ref, onMounted, onUnmounted, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import WebAppMenu from '../side-menu/WebAppMenu.vue';
import { WebAppMenuItems } from '../../../data/main-menu';
import { isEmbeddedContext, isEmbeddedRoute } from '@/utils/embeddedUtils';
import { navigateToCheckout } from '@/utils/checkoutNavigation';

export default defineComponent({
  name: 'WebAppLayout',
  components: { WebAppMenu },
  props: {
    title: { type: String, default: '' },
    showLogo: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    noPadding: { type: Boolean, default: false },
    hasGradient: { type: Boolean, default: false },
    whiteMode: { type: Boolean, default: false },
    stickyHeader: { type: Boolean, default: false },
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const isWebAppMenu = ref(false); // Initialize as closed instead of open
    const loading = ref(false);
    const isDesktop = ref(false);
    const isMobile = ref(false);

    const isWebRoute = computed(() => route.path.startsWith('/web'));
    const isEmbedded = computed(() => (
      props.embedded
      || isEmbeddedRoute(route.path)
      || isEmbeddedContext()
      || route.query.embedded === 'true'
      || !!route.query.embedOrganizationId
    ));
    const cartItemCount = computed(() => store.getters['cart/lineCount'] || 0);

    const showMobileCart = computed(() => (
      !isDesktop.value
      && !isEmbedded.value
      && !route.path.startsWith('/web/checkout')
    ));

    function goToCart() {
      navigateToCheckout(router, route);
    }

    function checkDesktop() {
      isDesktop.value = window.innerWidth >= 1024;
      isMobile.value = window.innerWidth < 768;
      const isCompleteProfile = route.path.startsWith('/auth/web/complete-profile');

      if (isEmbedded.value) {
        isWebAppMenu.value = false;
        return;
      }

      if (isDesktop.value && (isWebRoute.value || isCompleteProfile)) {
        isWebAppMenu.value = true;
      } else if (!isDesktop.value) {
        isWebAppMenu.value = false;
      }
    }

    function setMainMenuCollapse() {
      isWebAppMenu.value = !isWebAppMenu.value;
      store.commit('shared/SET_MAIN_MENU_COLLAPSE', !isWebAppMenu.value);
    }

    // Watch for route changes to close drawer on mobile
    watch(() => route.path, () => {
      if (!isDesktop.value) {
        isWebAppMenu.value = false;
      }
    });

    onMounted(() => {
      checkDesktop();
      window.addEventListener('resize', checkDesktop);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkDesktop);
    });

    return {
      // getter
      me: computed(() => store.getters['users/me']),

      // data
      user: {},
      isWebAppMenu,
      isDesktop,
      isMobile,
      isWebRoute,
      isEmbedded,
      WebAppMenuItems,
      setMainMenuCollapse,
      loading,
      cartItemCount,
      showMobileCart,
      goToCart,
    };
  },
});
</script>
<style lang="scss">
// Unified padding variables for all web app pages
:root {
  --web-app-padding-mobile: 24px;
  --web-app-padding-tablet: 40px;
  --web-app-padding-desktop: 64px;
}

// Override global grey background for web app
body {
  background: #ffffff !important;
}

#app {
  background: #ffffff !important;
}

.web-app-container {
    background: #fff;
    overflow-x: hidden;
    width: 100%;
    margin: 0;

    // Allow overflow when header needs to break out to full width
    &:has(.has-gradient-header) {
        overflow-x: visible;
    }

    .main-content_inner-wrap {
        background: #fff;
        padding: 0;
        overflow-x: hidden;

        // Allow overflow when header needs to break out to full width
        &:has(.has-gradient-header) {
            overflow-x: visible;
        }
    }
}
.overlay {
    position: fixed;
    top: 0;
    right: 0;
    width: 100dvw;
    height: 100%;
    cursor: pointer;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.4);
}
.logo-image {
    height: 28px;
    display: block;

    &.white-logo {
      filter: brightness(0) invert(1);
    }
}

@media (min-width: 1024px) {
  .overlay {
    display: none;
  }

  .logo-image {
    height: 33px;
  }
}

/* Embedded layout styles */
.embedded-layout {
  .main-content_inner-wrap {
    padding: 0;
  }

  .col-has-child {
    padding: 0;
  }

  /* Ensure embedded content takes full width */
  max-width: 100%;
  padding: 0;

  /* Remove any default margins/padding for embedded context */
  .web-app-body {
    padding: 0;
    margin: 0;
  }
}

/* No padding layout styles (for static pages and search) */
.no-padding-layout {
  .main-content_inner-wrap {
    padding: 0;
  }

  .web-app-sticky-header {
    @media (max-width: 767px) {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 9998;
      background: #fff;
      border-bottom: 1px solid #e5e7eb;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .web-app-mobile-header-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      margin-bottom: 0;

      @media (min-width: 768px) {
        padding: 24px 40px 0;
        margin-bottom: 1rem;
      }

      @media (min-width: 1024px) {
        padding: 24px 64px 0;
      }
    }

    .header-bar-leading {
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }

    .header-bar-center {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
      font-size: 1.125rem;
    }

    .header-bar-actions {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .header-menu-btn {
      cursor: pointer;
    }

    .mobile-header-leading-only {
      @media (min-width: 768px) {
        display: none;
      }
    }
  }

  .web-app-sticky-header-spacer {
    display: none;

    @media (max-width: 767px) {
      display: block;
      height: 64px;
    }
  }

  /* Keep padding for the header row (hamburger/logo/title) */
  .row.row-sm.align-items-center.mb-3 {
    padding: 20px 24px 0;

    .col {
      min-width: 0; // Prevent flex item from overflowing
    }

    .d-flex.align-items-center {
      gap: 0; // Remove any gap that might cause shifting
      justify-content: flex-start; // Always align to left
    }

    @media (min-width: 768px) {
      padding: 24px 40px 0;
    }

    @media (min-width: 1024px) {
      padding: 24px 64px 0;
    }

    &.has-gradient-header {
      background-color: #225c6b;
      padding: 20px 24px;
      margin-bottom: 0 !important;
      position: relative;
      z-index: 10; // Above the gradient (z-index: 0)
      box-sizing: border-box;

      // Break out of container to full viewport width
      width: 100vw;
      max-width: 100vw;
      margin-left: calc(50% - 50vw);
      margin-right: calc(50% - 50vw);

      @media (min-width: 768px) {
        padding: 20px 40px;
      }

      @media (min-width: 1024px) {
        padding: 20px 64px;

        // Counteract sidebar padding and span the full viewport
        &.has-sidebar {
          margin-left: -300px;
          margin-right: 0;
          width: 100vw;
          max-width: 100vw;
          padding-left: calc(300px + 64px);
        }
      }
    }

    // White mode for logo and icons when over gradient
    &.white-header-mode {
      .logo-image {
        filter: brightness(0) invert(1);
      }

      .white-icon {
        color: white !important;
      }

      .white-text {
        color: white !important;
      }
    }
  }

  .col-has-child {
    padding: 0;
    margin: 0;
  }

  /* Ensure content takes full width */
  .web-app-body {
    padding: 0;
    margin: 0;
  }

  /* Remove any gap between gradient header and content */
  .row.row-sm.align-items-center.mb-3.has-gradient-header + .grid-2-cols,
  .row.row-sm.align-items-center.mb-3.has-gradient-header + .col-has-child {
    margin-top: 0;
  }

  /* Ensure wrapper div doesn't add spacing */
  .main-content_inner-wrap > div {
    margin: 0;
  }
}

/* White mode styles */
.white-icon {
  color: white !important;
}

.white-text {
  color: white !important;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-back-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.2s;

  &:hover {
    background: #e5e7eb;
  }

  .back-icon {
    width: 18px;
    height: 18px;
    transform: rotate(180deg);
    opacity: 0.75;
  }
}

.header-cart-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0;

  &:hover {
    background: #e5e7eb;
  }

  &.white-icon-btn {
    background: rgba(255, 255, 255, 0.15);

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }

    img {
      filter: brightness(0) invert(1);
    }
  }

  .icon-with-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  img {
    width: 20px;
    height: 20px;
    opacity: 0.8;
  }

  .cart-badge {
    position: absolute;
    top: -6px;
    right: -8px;
    background: #ef4444;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    min-width: 16px;
    height: 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
}
</style>
